import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/config";
import {
  Save, ArrowLeft, Image, Type, Edit3, Upload, LogOut,
  Phone, Mail, Inbox, FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// File Upload Component
const FileUpload = ({ onUpload, label, currentImage }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const fullUrl = response.data.url;
      onUpload(fullUrl);
      toast.success("Afbeelding geupload!");
    } catch (error) {
      toast.error("Upload mislukt");
      setPreview(currentImage);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-4">
        <label className="flex-1 cursor-pointer">
          <div className="flex items-center justify-center gap-2 bg-[#0A0A0A] border border-[#27272A] border-dashed rounded-lg p-4 hover:border-[#FF6B00] transition-colors">
            <Upload className="w-5 h-5 text-[#A1A1AA]" />
            <span className="text-[#A1A1AA]">
              {uploading ? "Uploaden..." : "Kies bestand"}
            </span>
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            disabled={uploading}
          />
        </label>
      </div>
      {preview && (
        <img src={preview} alt="Preview" className="mt-2 h-32 rounded-lg object-cover" />
      )}
    </div>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [content, setContent] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [contentRes, quotesRes] = await Promise.all([
          axios.get(`${API}/content`),
          axios.get(`${API}/quotes`)
        ]);
        setContent(contentRes.data);
        setQuotes(quotesRes.data);
      } catch (error) {
        toast.error("Fout bij laden van content");
      } finally {
        setLoading(false);
      }
    };
    if (user) {
      fetchContent();
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, content);
      toast.success("Wijzigingen opgeslagen!");
    } catch (error) {
      toast.error("Fout bij opslaan");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Uitgelogd");
    navigate('/');
  };

  const updateField = (field, value) => {
    setContent({ ...content, [field]: value });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FF6B00] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Toaster position="top-right" theme="dark" />
      
      {/* Admin Header */}
      <div className="sticky top-0 z-50 bg-[#0A0A0A] border-b border-[#27272A] px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              data-testid="back-to-site"
              className="p-2 hover:bg-[#1A1A1A] rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold font-['Outfit']">Content Beheer</h1>
              <p className="text-sm text-[#A1A1AA]">Ingelogd als {user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-content-button"
              className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Opslaan..." : "Opslaan"}
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-[#27272A] text-white hover:bg-[#1A1A1A]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Uitloggen
            </Button>
          </div>
        </div>
      </div>

      {/* Admin Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        <Tabs defaultValue="hero" className="w-full">
          <TabsList className="grid w-full grid-cols-4 bg-[#1A1A1A] mb-8">
            <TabsTrigger value="hero" className="data-[state=active]:bg-[#FF6B00]">
              <Type className="w-4 h-4 mr-2" />
              Hero & Teksten
            </TabsTrigger>
            <TabsTrigger value="about" className="data-[state=active]:bg-[#FF6B00]">
              <Image className="w-4 h-4 mr-2" />
              Over Ons
            </TabsTrigger>
            <TabsTrigger value="images" className="data-[state=active]:bg-[#FF6B00]">
              <Upload className="w-4 h-4 mr-2" />
              Afbeeldingen
            </TabsTrigger>
            <TabsTrigger value="quotes" className="data-[state=active]:bg-[#FF6B00] relative">
              <Inbox className="w-4 h-4 mr-2" />
              Offertes
              {quotes.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF6B00] rounded-full text-[10px] font-bold flex items-center justify-center text-white">
                  {quotes.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="hero" className="space-y-6">
            <div className="bg-[#1A1A1A] rounded-xl p-6 border border-[#27272A]">
              <h3 className="text-lg font-bold font-['Outfit'] mb-4 flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-[#FF6B00]" />
                Hero Sectie
              </h3>
              <div className="grid gap-4">
                <div>
                  <Label htmlFor="hero_title">Hoofdtitel (wit)</Label>
                  <Input
                    id="hero_title"
                    data-testid="admin-hero-title"
                    value={content.hero_title}
                    onChange={(e) => updateField('hero_title', e.target.value)}
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-2"
                    placeholder="Vakmanschap in"
                  />
                </div>
                <div>
                  <Label htmlFor="hero_title_accent">Accent titel (oranje)</Label>
                  <Input
                    id="hero_title_accent"
                    data-testid="admin-hero-accent"
                    value={content.hero_title_accent}
                    onChange={(e) => updateField('hero_title_accent', e.target.value)}
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-2"
                    placeholder="Hout & Bouw"
                  />
                </div>
                <div>
                  <Label htmlFor="hero_subtitle">Subtitel</Label>
                  <Textarea
                    id="hero_subtitle"
                    data-testid="admin-hero-subtitle"
                    value={content.hero_subtitle}
                    onChange={(e) => updateField('hero_subtitle', e.target.value)}
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-2"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <div className="bg-[#1A1A1A] rounded-xl p-6 border border-[#27272A]">
              <h3 className="text-lg font-bold font-['Outfit'] mb-4">Statistieken</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="years_experience">Jaren ervaring</Label>
                  <Input
                    id="years_experience"
                    data-testid="admin-years"
                    value={content.years_experience}
                    onChange={(e) => updateField('years_experience', e.target.value)}
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-2"
                    placeholder="20+"
                  />
                </div>
                <div>
                  <Label htmlFor="happy_clients">Tevreden klanten</Label>
                  <Input
                    id="happy_clients"
                    data-testid="admin-clients"
                    value={content.happy_clients}
                    onChange={(e) => updateField('happy_clients', e.target.value)}
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-2"
                    placeholder="100+"
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="about" className="space-y-6">
            <div className="bg-[#1A1A1A] rounded-xl p-6 border border-[#27272A]">
              <h3 className="text-lg font-bold font-['Outfit'] mb-4 flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-[#FF6B00]" />
                Over Ons Sectie
              </h3>
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="owner_name">Naam eigenaar</Label>
                    <Input
                      id="owner_name"
                      data-testid="admin-owner-name"
                      value={content.owner_name}
                      onChange={(e) => updateField('owner_name', e.target.value)}
                      className="bg-[#0A0A0A] border-[#27272A] text-white mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="owner_title">Functie</Label>
                    <Input
                      id="owner_title"
                      data-testid="admin-owner-title"
                      value={content.owner_title}
                      onChange={(e) => updateField('owner_title', e.target.value)}
                      className="bg-[#0A0A0A] border-[#27272A] text-white mt-2"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="about_title">Sectie titel</Label>
                  <Input
                    id="about_title"
                    data-testid="admin-about-title"
                    value={content.about_title}
                    onChange={(e) => updateField('about_title', e.target.value)}
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="about_text1">Eerste paragraaf</Label>
                  <Textarea
                    id="about_text1"
                    data-testid="admin-about-text1"
                    value={content.about_text1}
                    onChange={(e) => updateField('about_text1', e.target.value)}
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-2"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="about_text2">Tweede paragraaf</Label>
                  <Textarea
                    id="about_text2"
                    data-testid="admin-about-text2"
                    value={content.about_text2}
                    onChange={(e) => updateField('about_text2', e.target.value)}
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-2"
                    rows={3}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="images" className="space-y-6">
            <div className="bg-[#1A1A1A] rounded-xl p-6 border border-[#27272A]">
              <h3 className="text-lg font-bold font-['Outfit'] mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-[#FF6B00]" />
                Afbeeldingen Uploaden
              </h3>
              <div className="grid gap-6">
                <FileUpload
                  label="Hero Achtergrond"
                  currentImage={content.hero_image}
                  onUpload={(url) => updateField('hero_image', url)}
                />
                <FileUpload
                  label="Profielfoto"
                  currentImage={content.about_image}
                  onUpload={(url) => updateField('about_image', url)}
                />
              </div>
            </div>

            <div className="bg-[#1A1A1A] rounded-xl p-6 border border-[#27272A]">
              <h3 className="text-lg font-bold font-['Outfit'] mb-4">Of via URL</h3>
              <div className="grid gap-4">
                <div>
                  <Label htmlFor="hero_image_url">Hero Achtergrond URL</Label>
                  <Input
                    id="hero_image_url"
                    value={content.hero_image}
                    onChange={(e) => updateField('hero_image', e.target.value)}
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-2"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label htmlFor="about_image_url">Profielfoto URL</Label>
                  <Input
                    id="about_image_url"
                    value={content.about_image}
                    onChange={(e) => updateField('about_image', e.target.value)}
                    className="bg-[#0A0A0A] border-[#27272A] text-white mt-2"
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="quotes" className="space-y-6">
            <div className="bg-[#1A1A1A] rounded-xl p-6 border border-[#27272A]">
              <h3 className="text-lg font-bold font-['Outfit'] mb-4 flex items-center gap-2">
                <Inbox className="w-5 h-5 text-[#FF6B00]" />
                Offerte Aanvragen ({quotes.length})
              </h3>
              {quotes.length === 0 ? (
                <div className="text-center py-12 text-[#A1A1AA]">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Nog geen offerte aanvragen ontvangen.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {[...quotes].reverse().map((quote) => (
                    <div
                      key={quote.id}
                      data-testid={`quote-item-${quote.id}`}
                      className="bg-[#0A0A0A] rounded-lg p-5 border border-[#27272A] hover:border-[#FF6B00]/30 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-white text-lg">{quote.name}</h4>
                          <span className="inline-block mt-1 px-3 py-0.5 bg-[#FF6B00]/10 text-[#FF6B00] text-xs font-medium rounded-full">
                            {quote.service}
                          </span>
                        </div>
                        <span className="text-xs text-[#A1A1AA] whitespace-nowrap">
                          {new Date(quote.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-[#A1A1AA] text-sm mb-3 whitespace-pre-wrap">{quote.message}</p>
                      <div className="flex flex-wrap gap-4 text-sm">
                        <a href={`mailto:${quote.email}`} className="text-[#FF6B00] hover:underline flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" /> {quote.email}
                        </a>
                        <a href={`tel:${quote.phone}`} className="text-[#FF6B00] hover:underline flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" /> {quote.phone}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
